/**
 * Gradebook view: sortable student x topic results. The "export-ready" table a
 * teacher would scan or hand to an administrator. Click a row to open the learner.
 */

import { useMemo, useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { ClassRoster, Learner } from './mockData';
import { attemptOf, topicMasteryOf } from './mockData';
import { MASTERY_META, Panel } from './charts';

type SortKey = 'roll' | 'name' | 'overall' | string; // string = topicId

const daysAgo = (n: number) => (n <= 0 ? 'Today' : n === 1 ? '1d ago' : `${n}d ago`);
const scoreColor = (p: number) => (p >= 70 ? '#1F6B5F' : p >= 55 ? '#9A7320' : '#B23B4E');

export function ResultsTable({ roster, onSelectLearner }: { roster: ClassRoster; onSelectLearner: (l: Learner) => void }) {
  const [sort, setSort] = useState<SortKey>('roll');
  const [dir, setDir] = useState<1 | -1>(1);

  const toggle = (key: SortKey) => {
    if (sort === key) setDir((d) => (d === 1 ? -1 : 1));
    else { setSort(key); setDir(key === 'name' || key === 'roll' ? 1 : -1); }
  };

  const rows = useMemo(() => {
    const val = (l: Learner): number | string => {
      if (sort === 'roll') return Number(l.rollNo);
      if (sort === 'name') return l.firstName;
      if (sort === 'overall') return l.overallMasteryPercent;
      return attemptOf(l, sort)?.scorePercent ?? -1; // topicId
    };
    return [...roster.learners].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * dir;
      return ((va as number) - (vb as number)) * dir;
    });
  }, [roster.learners, sort, dir]);

  const SortIcon = ({ k }: { k: SortKey }) => (
    sort !== k ? <ArrowUpDown className="h-3 w-3 opacity-40" /> : dir === 1 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
  );

  return (
    <Panel title="Gradebook" sub="Every learner across every topic. Click a column to sort, a row to open the learner.">
      <div className="-mx-2 overflow-x-auto px-2">
        <table className="w-full border-separate" style={{ borderSpacing: 0 }}>
          <thead>
            <tr className="text-[11px] font-bold uppercase tracking-wide text-[#8A8593]">
              <Th onClick={() => toggle('roll')} className="sticky left-0 z-10 bg-white text-center dark:bg-[#14141d]"><span className="inline-flex items-center gap-1">#<SortIcon k="roll" /></span></Th>
              <Th onClick={() => toggle('name')} className="sticky left-9 z-10 bg-white text-left dark:bg-[#14141d]"><span className="inline-flex items-center gap-1">Learner<SortIcon k="name" /></span></Th>
              {roster.topics.map((t) => (
                <Th key={t.id} onClick={() => toggle(t.id)} className="text-center">
                  <span className="inline-flex items-center gap-1">{t.title.split(' ')[0]}<SortIcon k={t.id} /></span>
                </Th>
              ))}
              <Th onClick={() => toggle('overall')} className="text-center"><span className="inline-flex items-center gap-1">Overall<SortIcon k="overall" /></span></Th>
              <th className="px-3 py-2 text-right">Active</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} onClick={() => onSelectLearner(l)}
                className="cursor-pointer text-[13px] [&:hover>td]:bg-black/[0.02] dark:[&:hover>td]:bg-white/[0.04]">
                <td className="sticky left-0 z-10 border-t border-black/[0.06] bg-white px-3 py-2.5 text-center text-[11.5px] font-semibold text-[#8A8593] dark:border-white/[0.08] dark:bg-[#14141d]">{l.rollNo}</td>
                <td className="sticky left-9 z-10 border-t border-black/[0.06] bg-white px-3 py-2.5 font-medium dark:border-white/[0.08] dark:bg-[#14141d]">{l.firstName}</td>
                {roster.topics.map((t) => {
                  const a = attemptOf(l, t.id);
                  const state = topicMasteryOf(l, t.id);
                  return (
                    <td key={t.id} className="border-t border-black/[0.06] px-3 py-2.5 text-center dark:border-white/[0.08]">
                      {a ? (
                        <span className="inline-flex items-center gap-1.5">
                          <i className="h-2 w-2 rounded-full" style={{ background: MASTERY_META[state].color }} />
                          <b className="tabular-nums" style={{ color: scoreColor(a.scorePercent) }}>{a.scorePercent}%</b>
                        </span>
                      ) : <span className="text-[11.5px] text-[#A9A7B4]">—</span>}
                    </td>
                  );
                })}
                <td className="border-t border-black/[0.06] px-3 py-2.5 text-center dark:border-white/[0.08]">
                  <b className="tabular-nums" style={{ color: scoreColor(l.overallMasteryPercent) }}>{l.overallMasteryPercent}%</b>
                </td>
                <td className="border-t border-black/[0.06] px-3 py-2.5 text-right text-[11.5px] text-[#8A8593] dark:border-white/[0.08]">{daysAgo(l.lastActiveDaysAgo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function Th({ children, onClick, className }: { children: React.ReactNode; onClick?: () => void; className?: string }) {
  return (
    <th onClick={onClick} className={cn('cursor-pointer select-none px-3 py-2 transition-colors hover:text-[#56505F] dark:hover:text-[#B2B0C0]', className)}>
      {children}
    </th>
  );
}
